import {
  isAnyDateField,
  isAnyTextField,
  isBooleanField,
  isCheckboxField,
  isDateTimeField,
  isLongTextField,
  isSelectField,
} from '@witmetrics/utils/forms';
import { isEmpty } from '@witmetrics/utils/misc';
import { mapToKey } from '@witmetrics/utils/arrays';
import { useFormField } from '@witmetrics/data-store';
import TextField from '@/components/inputs/TextField';
import DateTimeInput from '@/components/inputs/DateTimeInput';
import Toggle from '@/components/Toggle';
import SelectDropdown from '@/components/inputs/SelectDropdown';
import CheckboxDropdown from '@/components/inputs/CheckboxDropdown';
import { getCheckboxValues, getSelectValues } from './utils';

export type FieldInputProps = {
  className?: string;
  fieldId: number;
  value: string;
  onChange: (value: string) => void;
};

export default function FieldInput({
  className,
  fieldId,
  value,
  onChange,
}: FieldInputProps) {
  const field = useFormField(fieldId);

  const renderTextField = () => {
    return (
      <TextField
        multiline={isLongTextField(field!.formFieldTypeId)}
        className={className}
        minRows={3}
        maxRows={5}
        value={value}
        onChange={onChange}
      />
    );
  };

  const renderDateTimeInput = () => {
    return (
      <DateTimeInput
        closeOnSelect
        autoSet={false}
        disablePast={false}
        showTime={isDateTimeField(field!.formFieldTypeId)}
        className={className}
        value={isEmpty(value) ? undefined : value}
        label="Date"
        onChange={(update) => onChange(update || '')}
      />
    );
  };

  const renderBoolean = () => {
    return (
      <Toggle
        active={value === 'true'}
        className={className}
        onChange={(active) => onChange(active ? 'true' : 'false')}
      />
    );
  };

  const renderSelect = () => {
    const { currentOption, options } = getSelectValues(field!, value);
    return (
      <SelectDropdown
        className={className}
        value={currentOption}
        options={options}
        label="Select an option"
        onChange={(update) => onChange(update.value)}
      />
    );
  };

  const renderCheckbox = () => {
    const { currentOptions, options } = getCheckboxValues(field!, value);
    return (
      <CheckboxDropdown
        key={field!.id}
        name={field!.name}
        label={field!.label}
        value={currentOptions}
        options={options}
        onChange={(update) =>
          onChange(JSON.stringify(mapToKey(update, 'value')))
        }
      />
    );
  };

  if (!field) {
    console.warn(`FieldId "${fieldId}" not found`);
    return null;
  }

  if (isAnyTextField(field.formFieldTypeId)) return renderTextField();
  if (isAnyDateField(field.formFieldTypeId)) return renderDateTimeInput();
  if (isBooleanField(field.formFieldTypeId)) return renderBoolean();
  if (isSelectField(field.formFieldTypeId)) return renderSelect();
  if (isCheckboxField(field.formFieldTypeId)) return renderCheckbox();
  console.warn(`Unrecognized fieldTypeId: ${field.formFieldTypeId}`);
  return null;
}
